import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import type { ErrorCode } from '@api';
import { getAuthErrorMessageKey } from '@api/errors/constants/auth-error-message-keys.constant';
import { AppError, HandledAppError, isAppError, isErrorHandled } from '@api/errors/models/app-error.types';
import { LV2_PATHS } from '@core/navigation/navigation.constants';
import { queryParamsKeys } from '@core/navigation/query-param-key.contants';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import type {
  AccessTokenClaims,
  AccountStatus,
  AuthRefreshCoordination,
  AuthRefreshMessage,
  AuthRefreshResultMessage,
  AuthRefreshSuccessMessage,
  LoginRequest,
  LoginResponse,
  PendingRefreshResult,
  RecentRefreshResult,
  RefreshRequestContext,
  SessionContext,
  SessionTerminationReason,
  User,
} from './auth.types';
import { AuthenticationState } from './authentication.state';
import { SessionActivityService } from './session-activity.service';

const ACCOUNT_STATUS_BY_ERROR_CODE = {
  EMAIL_NOT_VERIFIED: 'non_verified',
  ACCOUNT_PENDING: 'pending',
  ACCOUNT_REJECTED: 'rejected',
  ACCOUNT_SUSPENDED: 'suspended',
} as const satisfies Partial<Record<ErrorCode, AccountStatus>>;

export type AccountStatusErrorCode = keyof typeof ACCOUNT_STATUS_BY_ERROR_CODE;

function isAccountStatusErrorCode(code: ErrorCode): code is AccountStatusErrorCode {
  return code in ACCOUNT_STATUS_BY_ERROR_CODE;
}

export class AccountStatusError extends Error {
  override readonly name = 'AccountStatusError';
  readonly accountStatus: (typeof ACCOUNT_STATUS_BY_ERROR_CODE)[AccountStatusErrorCode];

  constructor(readonly code: AccountStatusErrorCode) {
    super(code);
    this.accountStatus = ACCOUNT_STATUS_BY_ERROR_CODE[code];
  }
}
const REFRESH_LOCK_NAME = 'lv2-auth-refresh';
const REFRESH_CHANNEL_NAME = 'lv2-auth-refresh';
const REFRESH_RESULT_TIMEOUT_MS = 10_000;
const REFRESH_RESULT_MAX_AGE_MS = 10_000;
const AUTH_MUTATION_VERSION_KEY = 'lv2-auth-mutation-version';

let fallbackAuthMutationVersion = 0;

function readAuthMutationVersion(): number {
  try {
    const storedVersion = Number(globalThis.localStorage.getItem(AUTH_MUTATION_VERSION_KEY));
    return Number.isSafeInteger(storedVersion) && storedVersion >= 0
      ? Math.max(storedVersion, fallbackAuthMutationVersion)
      : fallbackAuthMutationVersion;
  } catch {
    return fallbackAuthMutationVersion;
  }
}

function advanceAuthMutationVersion(): number {
  const mutationVersion = readAuthMutationVersion() + 1;
  fallbackAuthMutationVersion = mutationVersion;
  try {
    globalThis.localStorage.setItem(AUTH_MUTATION_VERSION_KEY, mutationVersion.toString());
  } catch {
    // The in-memory fallback preserves ordering in the current tab when storage is unavailable.
  }
  return mutationVersion;
}

export const AUTH_REFRESH_COORDINATION = new InjectionToken<AuthRefreshCoordination | null>(
  'AUTH_REFRESH_COORDINATION',
  {
    factory: () => {
      if (
        typeof globalThis.navigator === 'undefined' ||
        globalThis.navigator.locks === undefined ||
        typeof globalThis.BroadcastChannel === 'undefined'
      ) {
        return null;
      }

      return {
        createChannel: () => new globalThis.BroadcastChannel(REFRESH_CHANNEL_NAME),
        requestLock: async <T>(callback: (lockAcquired: boolean) => Promise<T>) =>
          await globalThis.navigator.locks.request(REFRESH_LOCK_NAME, { ifAvailable: true }, (lock) =>
            callback(lock !== null),
          ),
        requestExclusiveLock: async <T>(callback: () => Promise<T>) =>
          await globalThis.navigator.locks.request(REFRESH_LOCK_NAME, callback),
        readMutationVersion: readAuthMutationVersion,
        advanceMutationVersion: advanceAuthMutationVersion,
      };
    },
  },
);

class AuthRefreshResultTimeoutError extends Error {}
class AuthRefreshCancelledError extends Error {}
class AuthRefreshSessionMismatchError extends Error {}
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private readonly _API_URL = environment.apiUrl;

  private readonly _currentUserSubject = new BehaviorSubject<User | null>(null);
  private readonly _http = inject(HttpClient);
  private readonly _refreshCoordination = inject(AUTH_REFRESH_COORDINATION);
  private readonly _refreshChannel = this._refreshCoordination?.createChannel() ?? null;
  private readonly _router = inject(Router);
  private readonly _authenticationState = inject(AuthenticationState);
  private readonly _sessionActivity = inject(SessionActivityService);
  private readonly _translate = inject(TranslateService);

  public readonly currentUser$ = this._currentUserSubject.asObservable();
  public readonly currentUser = toSignal(this.currentUser$);

  private _accessToken: string | null = null;
  private _accessTokenClaims: AccessTokenClaims | null = null;
  private _pendingRefreshResult: PendingRefreshResult | null = null;
  private _recentRefreshResult: RecentRefreshResult | null = null;
  private _receivedRefreshResult: AuthRefreshResultMessage | null = null;
  private _refreshContext: RefreshRequestContext | null = null;
  private _refreshPromise: Promise<string> | null = null;
  private _refreshAllowed = true;
  private _sessionGeneration = 0;
  private _sessionMutationVersion = 0;
  private _localAuthMutationTail: Promise<void> | null = null;

  constructor() {
    if (this._refreshChannel) {
      this._refreshChannel.onmessage = ({ data }) => this._handlePublishedRefresh(data);
    }
  }

  ngOnDestroy(): void {
    this._refreshChannel?.close();
  }

  /**
   * Authenticate user with email and password
   */
  async login(email: string, password: string): Promise<boolean> {
    const loginContext = this._captureSessionContext();
    try {
      const sessionContext = await this._runExclusiveAuthMutation(async (): Promise<SessionContext | null> => {
        if (loginContext.sessionGeneration !== this._sessionGeneration) {
          return null;
        }

        const loginData: LoginRequest = { email, password };
        const response = await firstValueFrom(
          this._http.post<LoginResponse>(`${this._API_URL}/auth/login`, loginData, { withCredentials: true }),
        );

        if (loginContext.sessionGeneration !== this._sessionGeneration) {
          return null;
        }

        if (!response?.accessToken) {
          throw new Error(this._translate.instant('auth.errors.tokenNotReceived'));
        }

        const mutationVersion = this._advanceMutationVersion();
        this._sessionActivity.reset();
        this._setAccessToken(response.accessToken, mutationVersion);
        this._authenticationState.setAuthenticationMethod(response.authenticationMethod ?? null);
        return this._captureSessionContext();
      });

      if (!sessionContext || !this._isSessionContextCurrent(sessionContext)) {
        return false;
      }

      await this.loadCurrentUser();

      return this._isSessionContextCurrent(sessionContext);
    } catch (error) {
      console.error('Erreur de connexion:', error);

      if (isErrorHandled(error)) {
        return false;
      }
      if (isAppError(error)) {
        const { code, params } = error;
        if (code && isAccountStatusErrorCode(code)) {
          throw new AccountStatusError(code);
        }
        if (code) {
          throw new Error(this._translate.instant(getAuthErrorMessageKey(code), params));
        }
        if (error.status === 422) {
          throw new Error(this._translate.instant('auth.errors.invalidLoginData'));
        }
      }

      throw new Error(this._translate.instant('auth.errors.unexpectedError'));
    }
  }

  /**
   * Load current user details from backend
   */
  async loadCurrentUser(): Promise<void> {
    const sessionContext = this._captureSessionContext();
    try {
      const user = await firstValueFrom(this._http.get<User>(`${this._API_URL}/users/me`));
      if (this._isSessionContextCurrent(sessionContext)) {
        this._currentUserSubject.next(user);
      }
    } catch (error) {
      if (!this._isSessionContextCurrent(sessionContext)) {
        return;
      }

      console.error('Error loading user details:', error);
      // If we can't load user details, clear auth state
      await this._performLogout(false);
      throw error;
    }
  }

  async restoreSession(): Promise<boolean> {
    try {
      await this.refreshAccessToken();
      await this.loadCurrentUser();
      return true;
    } catch {
      this.clearSession();
      return false;
    }
  }

  refreshAccessToken(failedAccessToken: string | null = null): Promise<string> {
    if (!this._refreshAllowed) {
      return Promise.reject(new AuthRefreshCancelledError('Refresh is disabled for the cleared session'));
    }

    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    const context: RefreshRequestContext = {
      failedAccessToken,
      sessionId: this._accessTokenClaims?.sid ?? null,
      mutationVersion: this._getRefreshMutationVersion(),
      sessionGeneration: this._sessionGeneration,
    };
    this._refreshContext = context;
    const refreshPromise = this._refreshAcrossTabs(context)
      .then((message) => {
        if (!this._isSessionContextCurrent(context)) {
          throw new AuthRefreshCancelledError('Session was cleared while refresh was pending');
        }

        this._setAccessToken(message.accessToken, message.mutationVersion);
        this._authenticationState.setAuthenticationMethod(message.authenticationMethod ?? null);
        return message.accessToken;
      })
      .catch((error: unknown) => {
        if (
          (context.failedAccessToken !== null || context.sessionId !== null) &&
          this._isSessionContextCurrent(context) &&
          this._shouldTerminateSessionAfterRefreshFailure(error)
        ) {
          this.clearSession();
          this._redirectToLogin('expired');
          if (isAppError(error)) {
            throw new HandledAppError(error);
          }
        }
        throw error;
      })
      .finally(() => {
        if (this._refreshContext === context) {
          this._refreshContext = null;
        }
        if (this._refreshPromise === refreshPromise) {
          this._refreshPromise = null;
        }
      });

    this._refreshPromise = refreshPromise;
    return refreshPromise;
  }

  /**
   * Logout user and clear the local session without waiting for the backend.
   */
  logout(): Promise<void> {
    return this._performLogout(true);
  }

  clearSessionInAllTabs(): void {
    const sessionId = this._accessTokenClaims?.sid ?? null;
    this.clearSession();
    this._refreshChannel?.postMessage({ type: 'logout', sessionId, mutationVersion: null });
    this._refreshChannel?.postMessage({
      type: 'logout',
      sessionId,
      mutationVersion: this._advanceMutationVersion(),
    });
  }

  /**
   * Check if user has a valid token
   */
  isAuthenticated(): boolean {
    return this._accessToken !== null;
  }

  /**
   * Get the current in-memory authentication token
   */
  getToken(): string | null {
    return this._accessToken;
  }

  clearSession(): void {
    this._sessionGeneration += 1;
    this._refreshAllowed = false;
    this._rejectPendingRefresh(new AuthRefreshCancelledError('Session was cleared'));
    this._recentRefreshResult = null;
    this._receivedRefreshResult = null;
    this._refreshContext = null;
    this._refreshPromise = null;
    this._accessToken = null;
    this._accessTokenClaims = null;
    this._currentUserSubject.next(null);
    this._authenticationState.resetAuthenticationMethod();
    this._sessionActivity.reset();
  }

  private _performLogout(redirectToLogin: boolean): Promise<void> {
    const token = this.getToken();
    const sessionId = this._accessTokenClaims?.sid ?? null;
    this.clearSession();
    this._refreshChannel?.postMessage({
      type: 'logout',
      sessionId,
      mutationVersion: null,
    });

    const backendLogout = this._runExclusiveAuthMutation(async () => {
      const mutationVersion = this._advanceMutationVersion();
      this._refreshChannel?.postMessage({
        type: 'logout',
        sessionId,
        mutationVersion,
      });
      await this._requestBackendLogout(token);
    });

    void backendLogout.catch((error) => {
      console.error('Error during backend logout:', error);
    });

    if (redirectToLogin) {
      this._redirectToLogin('logged-out');
    }

    return Promise.resolve();
  }

  private _requestBackendLogout(token: string | null): Promise<void> {
    const options = token
      ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      : { withCredentials: true };
    return firstValueFrom(this._http.post<void>(`${this._API_URL}/auth/logout`, null, options));
  }

  private _runExclusiveAuthMutation<T>(callback: () => Promise<T>): Promise<T> {
    if (this._refreshCoordination) {
      return this._refreshCoordination.requestExclusiveLock(callback);
    }

    const operation = this._localAuthMutationTail ? this._localAuthMutationTail.then(callback, callback) : callback();
    const mutationTail = operation.then(
      () => undefined,
      () => undefined,
    );
    this._localAuthMutationTail = mutationTail;
    void mutationTail.finally(() => {
      if (this._localAuthMutationTail === mutationTail) {
        this._localAuthMutationTail = null;
      }
    });
    return operation;
  }

  private _refreshAcrossTabs(context: RefreshRequestContext, retryCount = 0): Promise<AuthRefreshSuccessMessage> {
    if (!this._refreshCoordination || !this._refreshChannel) {
      return this._requestAndPublishRefresh(context);
    }

    return this._refreshCoordination
      .requestLock((lockAcquired) => {
        if (context.sessionGeneration !== this._sessionGeneration || !this._refreshAllowed) {
          return Promise.reject(new AuthRefreshCancelledError('Session was cleared before refresh coordination'));
        }

        const currentAccessToken = this.getToken();
        const currentSessionId = this._accessTokenClaims?.sid;
        if (
          context.failedAccessToken &&
          currentAccessToken &&
          currentSessionId &&
          context.failedAccessToken !== currentAccessToken
        ) {
          const currentMessage: AuthRefreshSuccessMessage = {
            type: 'refresh-succeeded',
            accessToken: currentAccessToken,
            sessionId: currentSessionId,
            mutationVersion: this._sessionMutationVersion,
            publishedAt: Date.now(),
            authenticationMethod: this._authenticationState.authenticationMethod(),
          };
          return Promise.resolve(currentMessage);
        }

        const receivedResult = this._getCompatibleReceivedRefresh(context);
        if (receivedResult) {
          return this._readRefreshResult(receivedResult);
        }

        if (!lockAcquired) {
          return this._waitForPublishedRefresh(context);
        }

        return this._requestAndPublishRefresh(context);
      })
      .catch((error: unknown) => {
        if (
          error instanceof AuthRefreshResultTimeoutError &&
          retryCount === 0 &&
          context.sessionGeneration === this._sessionGeneration &&
          this._refreshAllowed
        ) {
          return this._refreshAcrossTabs(context, retryCount + 1);
        }
        throw error;
      });
  }

  private async _requestAndPublishRefresh(context: RefreshRequestContext): Promise<AuthRefreshSuccessMessage> {
    try {
      const response = await firstValueFrom(
        this._http.post<LoginResponse>(`${this._API_URL}/auth/refresh`, null, { withCredentials: true }),
      );

      if (!response?.accessToken) {
        throw new Error(this._translate.instant('auth.errors.tokenNotReceived'));
      }
      const claims = this._decodeAccessTokenClaims(response.accessToken);
      if (!claims) {
        throw new Error(this._translate.instant('auth.errors.tokenNotReceived'));
      }

      if (context.sessionId !== null && claims.sid !== context.sessionId) {
        throw new AuthRefreshSessionMismatchError('The refresh result belongs to another session');
      }

      if (context.sessionGeneration !== this._sessionGeneration || !this._refreshAllowed) {
        throw new AuthRefreshCancelledError('Session was cleared while refresh was pending');
      }

      const message: AuthRefreshSuccessMessage = {
        type: 'refresh-succeeded',
        accessToken: response.accessToken,
        sessionId: claims.sid,
        mutationVersion: this._advanceMutationVersion(),
        publishedAt: Date.now(),
        authenticationMethod: response.authenticationMethod ?? null,
      };
      this._publishRefreshResult(message, context.sessionGeneration);
      return message;
    } catch (error) {
      if (this._isSessionContextCurrent(context) && this._shouldPublishRefreshFailure(error)) {
        this._publishRefreshResult(
          this._toRefreshFailureMessage(error, this._advanceMutationVersion()),
          context.sessionGeneration,
        );
      }
      throw error;
    }
  }

  private _publishRefreshResult(message: AuthRefreshResultMessage, sessionGeneration: number): void {
    this._recentRefreshResult = { message, publishedAt: Date.now(), sessionGeneration };
    this._receivedRefreshResult = message;
    this._refreshChannel?.postMessage(message);
  }

  private _waitForPublishedRefresh(context: RefreshRequestContext): Promise<AuthRefreshSuccessMessage> {
    return new Promise((resolve, reject) => {
      const timeout = globalThis.setTimeout(() => {
        this._pendingRefreshResult = null;
        reject(new AuthRefreshResultTimeoutError('Timed out while waiting for refresh result'));
      }, REFRESH_RESULT_TIMEOUT_MS);

      this._pendingRefreshResult = { ...context, resolve, reject, timeout };
      this._refreshChannel?.postMessage({
        type: 'refresh-request',
        failedAccessToken: context.failedAccessToken,
        sessionId: context.sessionId,
        mutationVersion: context.mutationVersion,
      });
    });
  }

  private _handlePublishedRefresh(message: AuthRefreshMessage): void {
    if (message.type === 'logout') {
      this._handlePublishedLogout(message);
      return;
    }

    if (message.type === 'refresh-request') {
      this._republishRecentRefreshResult(message);
      return;
    }

    if (
      !this._refreshAllowed ||
      message.mutationVersion <= this._sessionMutationVersion ||
      Date.now() - message.publishedAt > REFRESH_RESULT_MAX_AGE_MS
    ) {
      return;
    }

    if (this._receivedRefreshResult && message.mutationVersion <= this._receivedRefreshResult.mutationVersion) {
      return;
    }
    this._receivedRefreshResult = message;
    if (!this._pendingRefreshResult || !this._isRefreshResultCompatible(message, this._pendingRefreshResult)) {
      return;
    }

    if (this._pendingRefreshResult.sessionGeneration !== this._sessionGeneration) {
      this._rejectPendingRefresh(new AuthRefreshCancelledError('Session was cleared while awaiting refresh'));
      return;
    }

    if (message.type === 'refresh-succeeded') {
      this._resolvePendingRefresh(message);
      return;
    }

    this._rejectPendingRefresh(this._toRefreshHttpError(message));
  }

  private _handlePublishedLogout(message: Extract<AuthRefreshMessage, { type: 'logout' }>): void {
    const currentSessionId = this._accessTokenClaims?.sid ?? null;
    const matchesCurrentSession = currentSessionId !== null && currentSessionId === message.sessionId;
    const invalidatesInstalledSession =
      currentSessionId !== null &&
      message.mutationVersion !== null &&
      message.mutationVersion > this._sessionMutationVersion;
    const invalidatesPendingRestore =
      currentSessionId === null &&
      message.mutationVersion !== null &&
      this._refreshContext !== null &&
      message.mutationVersion > this._refreshContext.mutationVersion;

    if (!matchesCurrentSession && !invalidatesInstalledSession && !invalidatesPendingRestore) {
      return;
    }

    if (message.mutationVersion !== null) {
      this._sessionMutationVersion = Math.max(this._sessionMutationVersion, message.mutationVersion);
    }
    this.clearSession();
    this._redirectToLogin('logged-out');
  }

  private _republishRecentRefreshResult(request: Extract<AuthRefreshMessage, { type: 'refresh-request' }>): void {
    if (!this._refreshAllowed || !this._recentRefreshResult) {
      return;
    }

    const { message, publishedAt, sessionGeneration } = this._recentRefreshResult;
    if (sessionGeneration !== this._sessionGeneration || Date.now() - publishedAt > REFRESH_RESULT_MAX_AGE_MS) {
      this._recentRefreshResult = null;
      return;
    }

    if (!this._isRefreshResultCompatible(message, request)) {
      return;
    }

    this._refreshChannel?.postMessage(message);
  }

  private _resolvePendingRefresh(message: AuthRefreshSuccessMessage): void {
    if (!this._pendingRefreshResult) {
      return;
    }

    const { resolve, timeout } = this._pendingRefreshResult;
    this._pendingRefreshResult = null;
    globalThis.clearTimeout(timeout);
    resolve(message);
  }

  private _rejectPendingRefresh(error: Error): void {
    if (!this._pendingRefreshResult) {
      return;
    }

    const { reject, timeout } = this._pendingRefreshResult;
    this._pendingRefreshResult = null;
    globalThis.clearTimeout(timeout);
    reject(error);
  }

  private _getCompatibleReceivedRefresh(context: RefreshRequestContext): AuthRefreshResultMessage | null {
    if (!this._receivedRefreshResult) {
      return null;
    }

    if (Date.now() - this._receivedRefreshResult.publishedAt > REFRESH_RESULT_MAX_AGE_MS) {
      this._receivedRefreshResult = null;
      return null;
    }

    return this._isRefreshResultCompatible(this._receivedRefreshResult, context) ? this._receivedRefreshResult : null;
  }

  private _isRefreshResultCompatible(
    message: AuthRefreshResultMessage,
    request: Pick<RefreshRequestContext, 'failedAccessToken' | 'sessionId' | 'mutationVersion'>,
  ): boolean {
    if (
      message.mutationVersion <= request.mutationVersion ||
      (message.type === 'refresh-succeeded' && message.accessToken === request.failedAccessToken)
    ) {
      return false;
    }

    return request.sessionId === null || message.sessionId === request.sessionId;
  }

  private _readRefreshResult(message: AuthRefreshResultMessage): Promise<AuthRefreshSuccessMessage> {
    return message.type === 'refresh-succeeded'
      ? Promise.resolve(message)
      : Promise.reject(this._toRefreshHttpError(message));
  }

  private _toRefreshHttpError(message: Extract<AuthRefreshResultMessage, { type: 'refresh-failed' }>): AppError {
    return AppError.fromHttpError(
      new HttpErrorResponse({
        status: message.status,
        statusText: message.statusText,
        url: `${this._API_URL}/auth/refresh`,
        error: message.code ? { code: message.code } : null,
      }),
    );
  }

  private _toRefreshFailureMessage(failure: unknown, mutationVersion: number): AuthRefreshResultMessage {
    const error = isErrorHandled(failure) ? failure.error : failure;
    if (!isAppError(error)) {
      return {
        type: 'refresh-failed',
        status: 0,
        statusText: 'Unknown Error',
        code: null,
        sessionId: this._accessTokenClaims?.sid ?? null,
        mutationVersion,
        publishedAt: Date.now(),
      };
    }

    return {
      type: 'refresh-failed',
      status: error.status,
      statusText: error.httpError.statusText,
      code: error.code ?? null,
      sessionId: this._accessTokenClaims?.sid ?? null,
      mutationVersion,
      publishedAt: Date.now(),
    };
  }

  private _setAccessToken(token: string, mutationVersion: number): void {
    if (mutationVersion < this._sessionMutationVersion) {
      throw new AuthRefreshCancelledError('A newer authentication mutation is already installed');
    }

    const claims = this._decodeAccessTokenClaims(token);
    if (!claims) {
      throw new Error(this._translate.instant('auth.errors.tokenNotReceived'));
    }

    this._accessToken = token;
    this._accessTokenClaims = claims;
    this._sessionMutationVersion = mutationVersion;
    this._refreshAllowed = true;
  }

  private _getRefreshMutationVersion(): number {
    if (this._accessTokenClaims !== null) {
      return this._sessionMutationVersion;
    }

    return Math.max(this._sessionMutationVersion, this._refreshCoordination?.readMutationVersion() ?? 0);
  }

  private _advanceMutationVersion(): number {
    return this._refreshCoordination?.advanceMutationVersion() ?? this._sessionMutationVersion + 1;
  }

  private _captureSessionContext(): SessionContext {
    return {
      sessionId: this._accessTokenClaims?.sid ?? null,
      sessionGeneration: this._sessionGeneration,
    };
  }

  private _isSessionContextCurrent(context: SessionContext): boolean {
    return (
      this._refreshAllowed &&
      context.sessionGeneration === this._sessionGeneration &&
      context.sessionId === (this._accessTokenClaims?.sid ?? null)
    );
  }

  private _decodeAccessTokenClaims(token: string): AccessTokenClaims | null {
    try {
      const encodedPayload = token.split('.')[1];
      if (!encodedPayload) {
        return null;
      }

      const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(
        atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')),
      ) as Partial<AccessTokenClaims>;

      if (
        typeof payload.sub !== 'string' ||
        typeof payload.sid !== 'string' ||
        typeof payload.jti !== 'string' ||
        typeof payload.iat !== 'number' ||
        typeof payload.exp !== 'number'
      ) {
        return null;
      }

      return {
        sub: payload.sub,
        sid: payload.sid,
        jti: payload.jti,
        iat: payload.iat,
        exp: payload.exp,
      };
    } catch {
      return null;
    }
  }

  private _shouldTerminateSessionAfterRefreshFailure(error: unknown): boolean {
    return !(error instanceof AuthRefreshCancelledError || error instanceof AuthRefreshResultTimeoutError);
  }

  private _shouldPublishRefreshFailure(error: unknown): boolean {
    return (
      this._shouldTerminateSessionAfterRefreshFailure(error) && !(error instanceof AuthRefreshSessionMismatchError)
    );
  }

  private _redirectToLogin(reason: SessionTerminationReason): void {
    const queryParams: Record<string, string> = { sessionReason: reason };

    // An expired session is involuntary: bring the user back to the page they were on.
    const currentUrl = this._router.url;
    if (reason === 'expired') {
      queryParams[queryParamsKeys.REDIRECT_URL_AFTER_LOGIN_SUCCESS] = currentUrl;
    }

    void this._router.navigate([LV2_PATHS.LOGIN], {
      queryParams,
      replaceUrl: true,
    });
  }
}
